import { useState } from 'react';
import { api } from '../services/api';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2 } from 'lucide-react';
import TotvsLogo from '../components/TotvsLogo';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const senhaAtualizada = searchParams.get('senha_atualizada') === '1';

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao fazer login');
      login(data.token, data.user);
      try {
        // Timeout de 3s para não bloquear o redirect em ambientes lentos (CI, staging)
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000));
        const { count } = await Promise.race([api.getReconciliacaoPending(), timeout]);
        if (count > 0) { navigate('/reconciliacao'); return; }
      } catch { /* ignora erro de rede ou timeout */ }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center relative overflow-hidden pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]"
      style={{ background: 'linear-gradient(160deg, #001118 0%, #002233 100%)' }}>

      {/* Decorative glows */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,219,255,0.06) 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(138,68,255,0.06) 0%, transparent 70%)' }} />

      <div className="w-full max-w-sm px-6 relative">

        {/* Logo + title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <TotvsLogo height={28} color="white" />
          </div>
          <p className="text-xs font-semibold tracking-[0.22em] uppercase"
            style={{ color: 'rgba(0,219,255,0.55)' }}>
            Login CRM
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-6"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>

          {senhaAtualizada && (
            <div className="flex items-center gap-2 text-sm rounded-lg p-3 mb-4"
              style={{ background: 'rgba(0,212,163,0.12)', border: '1px solid rgba(0,212,163,0.25)', color: '#00D4A3' }}>
              <CheckCircle2 size={14} />
              Senha atualizada com sucesso.
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-1.5"
                style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.12em' }}>
                Usuário
              </label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoFocus
                required
                autoComplete="username"
                className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none transition-colors"
                style={{
                  background: 'rgba(0,219,255,0.06)',
                  border: '1px solid rgba(0,219,255,0.22)',
                  color: 'white',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(0,219,255,0.55)'}
                onBlur={e => e.target.style.borderColor = 'rgba(0,219,255,0.22)'}
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-1.5"
                style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.12em' }}>
                Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none transition-colors"
                style={{
                  background: 'rgba(0,219,255,0.06)',
                  border: '1px solid rgba(0,219,255,0.22)',
                  color: 'white',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(0,219,255,0.55)'}
                onBlur={e => e.target.style.borderColor = 'rgba(0,219,255,0.22)'}
              />
            </div>

            {error && (
              <p className="text-sm rounded-lg px-3 py-2"
                style={{ background: 'rgba(255,59,78,0.12)', border: '1px solid rgba(255,59,78,0.25)', color: '#FF3B4E' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg py-2.5 text-sm font-bold tracking-wide transition-all disabled:opacity-50"
              style={{
                background: '#00DBFF',
                color: '#002233',
                letterSpacing: '0.04em',
              }}
              onMouseEnter={e => !loading && (e.target.style.background = '#33E4FF')}
              onMouseLeave={e => (e.target.style.background = '#00DBFF')}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
